import { AbyssTheme as themeConfiguration } from '@src/app/browser';
import { chartColors } from '@src/shared/ui/components/Chart/includes/chartColors';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import { isEmpty, isUndefined, shuffle } from 'lodash';
import pattern from 'patternomaly';
import React, { useEffect, useMemo, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';

import { getTooltipFooter, getTooltipLabel, getTooltipTitle } from '../../includes/functions';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/**
 * DonutChart
 *
 * A donut chart is a circular statistical graphic that displays data in segments similar to a pie chart, with a blank
 * center resembling a donut.
 *
 * @param data
 * @param legendLimit
 * @param showLegend
 * @returns {React.JSX.Element|`\nValue: ${string}\nPercentage: ${*}%\n`|*}
 * @constructor
 */
export function DonutChart({ data, legendLimit, showLegend }) {
  const [labels, setLabels] = useState([]);
  const [datasets, setDatasets] = useState([]);

  /**
   * Defines an array of colors and patterns to use for rendering the datasets.
   *
   * @type {Array<CanvasPattern>}
   */
  const patterns = useMemo(() => {
    return pattern.generate(
      shuffle(chartColors).map((colorName) => {
        return themeConfiguration?.theme?.colors?.[`${colorName}`];
      })
    );
  }, [themeConfiguration]);

  /**
   * Configure the labels and datasets to be used by the chart.
   */
  useEffect(() => {
    if (!isEmpty(data)) {
      const theLabels = [];
      const theDataset = [];

      data?.forEach((item) => {
        const name = String(item?.name);
        theLabels.push(name);
        theDataset.push(item?.value);
      });

      if (theLabels !== labels) {
        setLabels(theLabels);
      }

      if (theDataset !== datasets) {
        setDatasets(theDataset);
      }
    }
  }, [data]);

  return (
    <ErrorHandler location="src/shared/ui/components/Chart/components/DonutChart/DonutChart.jsx">
      <Doughnut
        data={{
          datasets: [
            {
              backgroundColor: patterns,
              borderWidth: 1,
              data: datasets,
            },
          ],
          labels,
        }}
        options={{
          animation: {
            duration: 0, // general animation time
          },
          hover: {
            animationDuration: 0, // duration of animations when hovering an item
          },
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: isUndefined(showLegend) ? !isUndefined(legendLimit) && labels.length <= legendLimit : true,
            },
            tooltip: {
              callbacks: {
                footer(args) {
                  return getTooltipFooter(args, data);
                },
                label(args) {
                  return getTooltipLabel(args, data);
                },
                title(args) {
                  return getTooltipTitle(args, data);
                },
              },
            },
          },
          responsive: true,
          responsiveAnimationDuration: 0, // animation duration after a resize
        }}
      />
    </ErrorHandler>
  );
}
